import React, { useEffect } from "react";
import { Alert, Button, Divider, Form, message } from "antd";
import { RatingTable } from "./CreditAssessmentTables/RatingTable/RatingTable";
import { ReportingAnalysisTable } from "./CreditAssessmentTables/ReportingAnalysisTable/ReportingAnalysisTable";
import { AggregateBalanceTable } from "./CreditAssessmentTables/AggregateВalanceTable/AggregateВalanceTable";
import { EstablishedRatingTable } from "./CreditAssessmentTables/EstablishedRatingTable/EstablishedRatingTable";
import { AggregatedProfitAndLossReportTable } from "./CreditAssessmentTables/AggregatedProfitAndLossReportTable/AggregatedProfitAndLossReportTable";
import { useForm } from "antd/es/form/Form";
import { CommentForm } from "../../../../../UI_ITO/UI_ITOModuleCommentForm/CommentForm";
import { UI_ITOModuleSuccess } from "../../../../../UI_ITO/UI_ITOModuleSuccess/UI_ITOModuleSuccess";
import classes from "../StopFactors/StopFactors.module.css";
import { useParams } from "react-router-dom";
import { CreditworthinessEstablishedRating } from "../../../../../store/types/scoring.type";
import { APPLICATION_BANK_STATUS } from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { useGetApplicationDataToBankQuery } from "../../../../../store/api/applicationBankApi/applicationBank.api";
import { useChangeDataToScoringCreditWorthinessMutation } from "../../../../../store/api/applicationBankApi/scoring.api";

interface ICreditAssessmentFormFields {
  creditworthinessResults: CreditworthinessEstablishedRating[];
  commentCreditworthiness: string;
  commentSettlementRating: string;
}

export const CreditAssessment = () => {
  const [creditAssessmentForm] = useForm();
  const { applicationBankId } = useParams();

  const { data: applicationBank } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );

  const creditAssessmentData =
    applicationBank?.scoring_result.data.creditworthiness;
  const commentCreditworthiness =
    applicationBank?.scoring_result.data.commentCreditworthiness;
  const commentSettlementRating =
    applicationBank?.scoring_result.data.commentSettlementRating;

  // Обновляем поля формы в таблице "Установленный рейтинг организации" при изменении стора
  useEffect(() => {
    // если нет значений - присваивает из settlementRating
    const processedCreditworthlessResults =
      creditAssessmentData?.creditworthinessResults.map((row) => {
        if (row.title === "Установленный рейтинг" && row.value === "") {
          return {
            key: row.key,
            title: row.title,
            value:
              creditAssessmentData.settlementRating.find(
                (settlementRow) => settlementRow.title === "Расчетный рейтинг"
              )?.value || "",
          };
        }
        if (
          row.title === "Соответствующая оценка финансового положения" &&
          row.value === ""
        ) {
          return {
            key: row.key,
            title: row.title,
            value:
              creditAssessmentData.settlementRating.find(
                (settlementRow) =>
                  settlementRow.title ===
                  "Соответствующая оценка финансового положения"
              )?.value || "",
          };
        }
        return row;
      });

    creditAssessmentForm.setFieldsValue({
      creditworthinessResults: processedCreditworthlessResults,
      commentCreditworthiness: commentCreditworthiness ?? "",
      commentSettlementRating: commentSettlementRating ?? "",
    });
  }, [
    creditAssessmentData,
    commentCreditworthiness,
    commentSettlementRating,
    creditAssessmentForm,
  ]);

  const [changeData, { isSuccess, isLoading }] =
    useChangeDataToScoringCreditWorthinessMutation();

  const handleFinish = (formFields: ICreditAssessmentFormFields) => {
    if (applicationBankId) {
      changeData({
        app_id: +applicationBankId,
        data: formFields,
      })
        .unwrap()
        .then(() => {
          message.success("Комментарии добавлены");
        })
        .catch(() => {
          message.error("Комментарии не добавлены");
        });
    }
  };

  if (!creditAssessmentData) {
    return (
      <Alert
        message="Для получения данных об оценке кредитоспособности заполните бизнес-риски"
        type="info"
        showIcon
      />
    );
  }

  const {
    creditworthinessResults,
    aggregatedProfitAndLossReport,
    reportingAnalysis,
    aggregatedBalanceSheet,
    settlementRating,
  } = creditAssessmentData;

  return (
    <Form
      autoComplete="false"
      form={creditAssessmentForm}
      initialValues={{
        creditworthinessResults,
        commentCreditworthiness,
        commentSettlementRating,
      }}
      onFinish={handleFinish}
    >
      {settlementRating && (
        <>
          <Divider orientation="left" orientationMargin="0">
            Расчётный рейтинг организации
          </Divider>
          <RatingTable dataSource={settlementRating} />
          <CommentForm
            name={"commentSettlementRating"}
            title={"Комментарий"}
            disabled={
              applicationBank?.modified_bank_status.type ===
              APPLICATION_BANK_STATUS.NEW_APPLICATION
            }
          />
        </>
      )}

      {creditworthinessResults && (
        <>
          <Divider orientation="left" orientationMargin="0">
            Установленный рейтинг организации
          </Divider>
          <EstablishedRatingTable dataSource={creditworthinessResults} />
        </>
      )}

      {aggregatedBalanceSheet && (
        <>
          <Divider orientation="left" orientationMargin="0">
            Агрегированный бухгалтерский баланс
          </Divider>
          <AggregateBalanceTable dataSource={aggregatedBalanceSheet} />
        </>
      )}

      {aggregatedProfitAndLossReport && (
        <>
          <Divider orientation="left" orientationMargin="0">
            Агрегированный отчёт о прибылях и убытках
          </Divider>
          <AggregatedProfitAndLossReportTable
            columns={aggregatedProfitAndLossReport.columns}
            dataSource={aggregatedProfitAndLossReport.dataSource}
          />
        </>
      )}

      {reportingAnalysis && (
        <>
          <Divider orientation="left" orientationMargin="0">
            Коэффициентный анализ отчётности организации
          </Divider>
          <ReportingAnalysisTable dataSource={reportingAnalysis} />
        </>
      )}

      <CommentForm
        name="commentCreditworthiness"
        disabled={
          applicationBank?.modified_bank_status.type ===
          APPLICATION_BANK_STATUS.NEW_APPLICATION
        }
      />
      <div className={classes.submitWrapper}>
        <UI_ITOModuleSuccess
          isSuccess={isSuccess}
          title="Данные успешно сохранены"
        />
        <Button htmlType="submit" type="primary" loading={isLoading}>
          Сохранить
        </Button>
      </div>
    </Form>
  );
};
